/**
 * What external providers cost us: the rate card, and the prepaid
 * agreements its pooled rates come from.
 *
 * Most providers sell a pool — so many checks or searches a year for one
 * price — so a call has no price of its own. An agreement records what was
 * paid and for what; the server divides the price over the allowance and puts
 * the resulting per-unit rate on the rate card. Those rates can only be
 * changed through their agreement. A provider billed at a plain per-unit price
 * gets a rate entered by hand.
 *
 * A rate card key is `provider / service / unit`, spelt exactly as usage is
 * recorded (`castellum / screen / checks`).
 *
 * Money and rates arrive as decimal strings and are parsed to numbers for
 * display; writes send them back as strings, so a rate is never rounded by
 * floating point on its way to the server.
 */

/** The providers we hold a contract with. Mirrors the backend's `ExternalProvider`. */
export const EXTERNAL_PROVIDERS = [
  'armadillo',
  'castellum',
  'ferretly',
  'lexis-nexis',
  'moodys',
  'osint-industries',
  'pacer',
  'tavily',
] as const;

export type ExternalProvider = (typeof EXTERNAL_PROVIDERS)[number];

export const PROVIDER_LABELS: Readonly<Record<ExternalProvider, string>> = {
  armadillo: 'Armadillo',
  castellum: 'Castellum',
  ferretly: 'Ferretly',
  'lexis-nexis': 'LexisNexis',
  moodys: "Moody's",
  'osint-industries': 'OSINT Industries',
  pacer: 'PACER',
  tavily: 'Tavily',
};

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider as ExternalProvider] ?? provider;
}

function toNumber(value: string | number): number {
  return typeof value === 'number' ? value : Number.parseFloat(value);
}

function toNullableNumber(value: string | number | null | undefined): number | null {
  return value === null || value === undefined ? null : toNumber(value);
}

// --- Rates ---

export interface ProviderRate {
  id: string;
  provider: string;
  service: string;
  unitKey: string;
  costPerUnit: number;
  currency: string;
  /** Inclusive, as an ISO date. */
  effectiveFrom: string;
  /** Inclusive; null while the rate still applies. */
  effectiveTo: string | null;
  /** Set when the rate comes from an agreement, which is then the only way to change it. */
  providerAgreementId: string | null;
  notes: string | null;
  createdByUserId: string | null;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiProviderRate {
  id: string;
  provider: string;
  service: string;
  unit_key: string;
  cost_per_unit: string | number;
  currency: string;
  effective_from: string;
  effective_to: string | null;
  provider_agreement_id: string | null;
  notes: string | null;
  created_by_user_id: string | null;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export function transformToProviderRate(api: ApiProviderRate): ProviderRate {
  return {
    id: api.id,
    provider: api.provider,
    service: api.service,
    unitKey: api.unit_key,
    costPerUnit: toNumber(api.cost_per_unit),
    currency: api.currency,
    effectiveFrom: api.effective_from,
    effectiveTo: api.effective_to,
    providerAgreementId: api.provider_agreement_id,
    notes: api.notes,
    createdByUserId: api.created_by_user_id,
    updatedByUserId: api.updated_by_user_id,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

/** Whether the rate is in effect on `day` (an ISO date). */
export function isRateActiveOn(rate: ProviderRate, day: string): boolean {
  return rate.effectiveFrom <= day && (rate.effectiveTo === null || day <= rate.effectiveTo);
}

/** A new hand-entered rate. Starting after an open-ended rate for the same
 * key closes that one the day before. */
export interface ProviderRateInput {
  provider: ExternalProvider;
  service: string;
  unitKey: string;
  /** Kept as typed, so no digit is lost to floating point. */
  costPerUnit: string;
  currency: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  notes: string | null;
}

export interface ApiProviderRateCreate {
  provider: string;
  service: string;
  unit_key: string;
  cost_per_unit: string;
  currency: string;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
}

export function toApiProviderRateCreate(input: ProviderRateInput): ApiProviderRateCreate {
  return {
    provider: input.provider,
    service: input.service.trim(),
    unit_key: input.unitKey.trim(),
    cost_per_unit: input.costPerUnit.trim(),
    currency: input.currency,
    effective_from: input.effectiveFrom,
    effective_to: input.effectiveTo,
    notes: input.notes?.trim() || null,
  };
}

/** A correction to a hand-entered rate. Only the fields present are sent;
 * `effectiveTo: null` reopens the rate. The key cannot change. */
export interface ProviderRateUpdate {
  costPerUnit?: string;
  currency?: string;
  effectiveFrom?: string;
  effectiveTo?: string | null;
  notes?: string | null;
}

export function toApiProviderRateUpdate(update: ProviderRateUpdate): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (update.costPerUnit !== undefined) {
    body.cost_per_unit = update.costPerUnit.trim();
  }
  if (update.currency !== undefined) {
    body.currency = update.currency;
  }
  if (update.effectiveFrom !== undefined) {
    body.effective_from = update.effectiveFrom;
  }
  if (update.effectiveTo !== undefined) {
    body.effective_to = update.effectiveTo;
  }
  if (update.notes !== undefined) {
    body.notes = update.notes?.trim() || null;
  }
  return body;
}

// --- Agreements ---

export interface ProviderAgreementLine {
  id: string;
  service: string;
  unitKey: string;
  allowance: number;
  /** Set on every line when the contract prices each kind separately; null
   * on every line when one price buys a shared pool. */
  priceShare: number | null;
  /** Cost per unit beyond the allowance; null when the contract sets none. */
  overageRate: number | null;
  /** The rate this line puts on the rate card. */
  costPerUnit: number;
}

export interface ProviderAgreement {
  id: string;
  provider: string;
  startsOn: string;
  endsOn: string;
  price: number;
  currency: string;
  notes: string | null;
  /** True when one price buys a pool every line draws from. */
  sharedPool: boolean;
  lines: ProviderAgreementLine[];
  createdByUserId: string | null;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiProviderAgreementLine {
  id: string;
  service: string;
  unit_key: string;
  allowance: number;
  price_share: string | number | null;
  overage_rate: string | number | null;
  cost_per_unit: string | number;
}

export interface ApiProviderAgreement {
  id: string;
  provider: string;
  starts_on: string;
  ends_on: string;
  price: string | number;
  currency: string;
  notes: string | null;
  shared_pool: boolean;
  lines: ApiProviderAgreementLine[];
  created_by_user_id: string | null;
  updated_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export function transformToProviderAgreement(api: ApiProviderAgreement): ProviderAgreement {
  return {
    id: api.id,
    provider: api.provider,
    startsOn: api.starts_on,
    endsOn: api.ends_on,
    price: toNumber(api.price),
    currency: api.currency,
    notes: api.notes,
    sharedPool: api.shared_pool,
    lines: api.lines.map((line) => ({
      id: line.id,
      service: line.service,
      unitKey: line.unit_key,
      allowance: line.allowance,
      priceShare: toNullableNumber(line.price_share),
      overageRate: toNullableNumber(line.overage_rate),
      costPerUnit: toNumber(line.cost_per_unit),
    })),
    createdByUserId: api.created_by_user_id,
    updatedByUserId: api.updated_by_user_id,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

/** A whole agreement as the form holds it. Saving replaces its lines and the
 * rates derived from them. Money is kept as typed. */
export interface ProviderAgreementLineInput {
  service: string;
  unitKey: string;
  allowance: number;
  priceShare: string | null;
  overageRate: string | null;
}

export interface ProviderAgreementInput {
  provider: ExternalProvider;
  startsOn: string;
  endsOn: string;
  price: string;
  currency: string;
  notes: string | null;
  lines: ProviderAgreementLineInput[];
}

export interface ApiProviderAgreementWrite {
  provider: string;
  starts_on: string;
  ends_on: string;
  price: string;
  currency: string;
  notes: string | null;
  lines: {
    service: string;
    unit_key: string;
    allowance: number;
    price_share: string | null;
    overage_rate: string | null;
  }[];
}

const blankToNull = (value: string | null): string | null => value?.trim() || null;

export function toApiProviderAgreementWrite(
  input: ProviderAgreementInput
): ApiProviderAgreementWrite {
  return {
    provider: input.provider,
    starts_on: input.startsOn,
    ends_on: input.endsOn,
    price: input.price.trim(),
    currency: input.currency,
    notes: blankToNull(input.notes),
    lines: input.lines.map((line) => ({
      service: line.service.trim(),
      unit_key: line.unitKey.trim(),
      allowance: line.allowance,
      price_share: blankToNull(line.priceShare),
      overage_rate: blankToNull(line.overageRate),
    })),
  };
}

/** The agreement as the form starts from when editing it. */
export function toProviderAgreementInput(agreement: ProviderAgreement): ProviderAgreementInput {
  return {
    provider: agreement.provider as ExternalProvider,
    startsOn: agreement.startsOn,
    endsOn: agreement.endsOn,
    price: String(agreement.price),
    currency: agreement.currency,
    notes: agreement.notes,
    lines: agreement.lines.map((line) => ({
      service: line.service,
      unitKey: line.unitKey,
      allowance: line.allowance,
      priceShare: line.priceShare === null ? null : String(line.priceShare),
      overageRate: line.overageRate === null ? null : String(line.overageRate),
    })),
  };
}
